import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Представляет состояние интервала техобслуживания
 */
export interface MaintenanceState {
  /** Текстовое описание состояния (например, "Пробег: Осталось 1000 км") */
  text: string;
  /** Цвет состояния: 'red' - просрочено, 'green' - осталось, 'gray' - прочерк */
  color: 'red' | 'green' | 'gray';
  /** Числовое значение остатка (отрицательное при просрочке) */
  value: number;
  /** Тип интервала: 'mileage' | 'engineHours' | 'days' */
  type: string;
}

/**
 * Константы для вычисления временных интервалов
 */
const TIME_CONSTANTS = {
  /** Количество миллисекунд в одном часе */
  MS_PER_HOUR: 1000 * 60 * 60,
  /** Количество миллисекунд в одном дне */
  MS_PER_DAY: 1000 * 60 * 60 * 24,
  /** Порог для отображения в днях вместо часов */
  HOURS_THRESHOLD: 24,
} as const;

/**
 * Константы для правильного склонения слов (русский язык)
 */
const PLURALIZATION = {
  /** Диапазон для множественного числа (11-19) */
  TEENS_RANGE: { min: 11, max: 19 },
  /** Окончание для единственного числа (1) */
  SINGULAR: 1,
  /** Диапазон для "мало" (2-4) */
  FEW_RANGE: { min: 2, max: 4 },
} as const;

/**
 * Pure pipe для вычисления состояния интервалов техобслуживания объекта
 *
 * Формула расчета: (последний раз + интервал - текущее значение)
 * - Если результат >= 0 → "Осталось ..." (зеленый)
 * - Если результат < 0 → "Просрочено на ..." (красный)
 *
 * Поддерживает три типа интервалов:
 * - Пробег (в километрах)
 * - Моточасы (в часах)
 * - Дни (в днях/часах)
 *
 * @example
 * ```html
 * <div *ngIf="item | maintenanceState: currentLang() as states">
 *   <div *ngFor="let state of states">
 *     <span [style.color]="state.color">
 *       {{ state.text }}
 *     </span>
 *   </div>
 * </div>
 * ```
 */
@Pipe({
  name: 'maintenanceState',
  standalone: true,
  pure: true,
})
export class MaintenanceStatePipe implements PipeTransform {
  private readonly translate = inject(TranslateService);

  private get mileageLabel(): string {
    return this.translate.instant('Mileage');
  }

  private get engineHoursLabel(): string {
    return this.translate.instant('Engine hours');
  }

  private get daysLabel(): string {
    return this.translate.instant('DAYS');
  }

  private get expiredByLabel(): string {
    return this.translate.instant('Expired by');
  }

  private get remainsLabel(): string {
    return this.translate.instant('Remains');
  }

  private get kmLabel(): string {
    return this.translate.instant('km');
  }

  private get mhLabel(): string {
    return this.translate.instant('m/h');
  }

  /**
   * Преобразует объект интервала техобслуживания в массив состояний
   *
   * @param item - Объект с настройками интервалов техобслуживания
   * @param item.settings - Настройки интервалов (mileage, engineHours, days)
   * @param lang - Текущий язык: не читается, нужен только как вход, чтобы pure-пайп пересчитался
   * @returns Массив состояний включенных интервалов или null если нет активных интервалов
   */
  transform(item: any, lang?: string | null): MaintenanceState[] | null {
    if (!item?.settings) {
      return null;
    }

    const settings = item.settings;
    const results: MaintenanceState[] = [];

    // Вычисляем состояние для каждого типа интервала
    if (settings.mileage?.enabled) {
      const mileageState = this.calculateMileageState(settings.mileage);
      if (mileageState) {
        results.push(mileageState);
      }
    }

    if (settings.engineHours?.enabled) {
      const engineHoursState = this.calculateEngineHoursState(settings.engineHours);
      if (engineHoursState) {
        results.push(engineHoursState);
      }
    }

    if (settings.days?.enabled && settings.days.last) {
      const daysState = this.calculateDaysState(settings.days);
      if (daysState) {
        results.push(daysState);
      }
    }

    return results.length > 0 ? results : null;
  }

  /**
   * Вычисляет состояние интервала по пробегу
   *
   * @param mileageSettings - Настройки пробега
   * @returns Состояние интервала по пробегу
   */
  private calculateMileageState(mileageSettings: any): MaintenanceState {
    const last = Number(mileageSettings.last) || 0;
    const interval = Number(mileageSettings.interval) || 0;
    const current = Number(mileageSettings.current) || 0;

    // Если текущий пробег меньше последнего обслуживания - возвращаем прочерк
    if (current < last) {
      return {
        text: `${this.mileageLabel}: —`,
        color: 'gray',
        value: 0,
        type: 'mileage',
      };
    }

    const remaining = last + interval - current;
    const isOverdue = remaining < 0;
    const km = Math.abs(Math.round(remaining));

    const statusText = isOverdue
      ? `${this.expiredByLabel} ${km}${this.kmLabel}`
      : `${this.remainsLabel} ${km}${this.kmLabel}`;

    return {
      text: `${this.mileageLabel}: ${statusText}`,
      color: isOverdue ? 'red' : 'green',
      value: remaining,
      type: 'mileage',
    };
  }

  /**
   * Вычисляет состояние интервала по моточасам
   *
   * @param engineHoursSettings - Настройки моточасов
   * @returns Состояние интервала по моточасам
   */
  private calculateEngineHoursState(engineHoursSettings: any): MaintenanceState {
    const last = Number(engineHoursSettings.last) || 0;
    const interval = Number(engineHoursSettings.interval) || 0;
    const current = Number(engineHoursSettings.current) || 0;

    // Если текущие моточасы меньше последнего обслуживания - возвращаем прочерк
    if (current < last) {
      return {
        text: `${this.engineHoursLabel}: —`,
        color: 'gray',
        value: 0,
        type: 'engineHours',
      };
    }

    const remaining = last + interval - current;
    const isOverdue = remaining < 0;
    const hours = Math.abs(Math.round(remaining));

    const statusText = isOverdue
      ? `${this.expiredByLabel} ${hours} ${this.mhLabel}`
      : `${this.remainsLabel} ${hours} ${this.mhLabel}`;

    return {
      text: `${this.engineHoursLabel}: ${statusText}`,
      color: isOverdue ? 'red' : 'green',
      value: remaining,
      type: 'engineHours',
    };
  }

  /**
   * Вычисляет состояние интервала по дням
   *
   * Формула: Следующее событие = Время последнего события + (Количество дней × 24 часа)
   *
   * Особенности:
   * 1. Сначала считаем разницу в днях без учета времени (обнуляем часы/минуты/секунды)
   * 2. Если остался ровно 1 день (или просрочено на 1 день) - переключаемся на расчет в часах
   * 3. Для расчета в часах используем полную дату-время (с часами и минутами)
   *
   * @param daysSettings - Настройки интервала по дням
   * @returns Состояние интервала по дням или null если дата невалидна
   */
  private calculateDaysState(daysSettings: any): MaintenanceState | null {
    const interval = Number(daysSettings.interval) || 0;
    const lastDateTime = new Date(daysSettings.last);
    const nowUtc = new Date();

    if (isNaN(lastDateTime.getTime())) {
      return null;
    }

    // Последняя дата + интервал в днях
    const targetDateTime = new Date(lastDateTime.getTime() + interval * TIME_CONSTANTS.MS_PER_DAY);

    // Считаем реальную разницу между timestamp
    const diffMs = targetDateTime.getTime() - nowUtc.getTime();

    // Отбрасываем оставшиеся часы
    let computedToNext = Math.floor(diffMs / TIME_CONSTANTS.MS_PER_DAY);

    let isOverdue = computedToNext < 0;
    let inHours = false;

    // Если остался 0 или 1 день (или просрочено на 0 или 1 день) - переключаемся на часы
    if (Math.abs(computedToNext) <= 1) {
      // Для расчета в часах используем полную дату-время
      const targetDateTime = new Date(
        lastDateTime.getTime() + interval * TIME_CONSTANTS.MS_PER_DAY,
      );
      const hoursComputed = Math.floor(
        (targetDateTime.getTime() - nowUtc.getTime()) / TIME_CONSTANTS.MS_PER_HOUR,
      );

      // Используем часы только если их меньше 24
      if (Math.abs(hoursComputed) < TIME_CONSTANTS.HOURS_THRESHOLD) {
        inHours = true;
        computedToNext = hoursComputed;
      }
    }

    // Обрабатываем просрочку
    if (computedToNext < 0) {
      computedToNext = Math.abs(computedToNext);
      isOverdue = true;
    }

    // Форматируем результат
    const statusText = inHours
      ? this.formatHours(computedToNext, isOverdue)
      : this.formatDays(computedToNext, isOverdue);

    return {
      text: `${this.daysLabel}: ${statusText}`,
      color: isOverdue ? 'red' : 'green',
      value: inHours ? computedToNext : computedToNext * 24, // Сохраняем в часах для совместимости
      type: 'days',
    };
  }

  /**
   * Форматирует количество дней в читаемый вид
   *
   * @param days - Количество дней
   * @param isOverdue - Флаг просрочки
   * @returns Отформатированная строка
   */
  private formatDays(days: number, isOverdue: boolean): string {
    return isOverdue
      ? `${this.expiredByLabel} ${days} ${this.getDaysWord(days)}`
      : `${this.remainsLabel} ${days} ${this.getDaysWord(days)}`;
  }

  /**
   * Форматирует количество часов в читаемый вид
   *
   * @param hours - Количество часов
   * @param isOverdue - Флаг просрочки
   * @returns Отформатированная строка
   */
  private formatHours(hours: number, isOverdue: boolean): string {
    return isOverdue
      ? `${this.expiredByLabel} ${hours} ${this.getHoursWord(hours)}`
      : `${this.remainsLabel} ${hours} ${this.getHoursWord(hours)}`;
  }

  /**
   * Возвращает правильное склонение слова "день" в зависимости от числа
   *
   * @param count - Количество дней
   * @returns Склоненное слово: "день" | "дня" | "дней"
   */
  private getDaysWord(count: number): string {
    return this.pluralizeRussian(
      count,
      this.translate.instant('day_1'),
      this.translate.instant('day_2'),
      this.translate.instant('day_3'),
    );
  }

  /**
   * Возвращает правильное склонение слова "час" в зависимости от числа
   *
   * @param count - Количество часов
   * @returns Склоненное слово: "час" | "часа" | "часов"
   */
  private getHoursWord(count: number): string {
    return this.pluralizeRussian(
      count,
      this.translate.instant('hour_1'),
      this.translate.instant('hour_2'),
      this.translate.instant('hour_3'),
    );
  }

  /**
   * Универсальная функция для склонения слов в русском языке
   *
   * @param count - Количество
   * @param one - Форма для 1 (например: "час")
   * @param few - Форма для 2-4 (например: "часа")
   * @param many - Форма для 5+ и 11-19 (например: "часов")
   * @returns Правильно склоненное слово
   *
   * @example
   * pluralizeRussian(1, 'час', 'часа', 'часов') // 'час'
   * pluralizeRussian(2, 'час', 'часа', 'часов') // 'часа'
   * pluralizeRussian(5, 'час', 'часа', 'часов') // 'часов'
   * pluralizeRussian(11, 'час', 'часа', 'часов') // 'часов'
   */
  private pluralizeRussian(count: number, one: string, few: string, many: string): string {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    // Особый случай для чисел 11-19
    if (
      lastTwoDigits >= PLURALIZATION.TEENS_RANGE.min &&
      lastTwoDigits <= PLURALIZATION.TEENS_RANGE.max
    ) {
      return many;
    }

    // Единственное число (1, 21, 31, ...)
    if (lastDigit === PLURALIZATION.SINGULAR) {
      return one;
    }

    // "Мало" (2-4, 22-24, 32-34, ...)
    if (lastDigit >= PLURALIZATION.FEW_RANGE.min && lastDigit <= PLURALIZATION.FEW_RANGE.max) {
      return few;
    }

    // Множественное число (0, 5-20, 25-30, ...)
    return many;
  }
}
